import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PlayerInfoView } from '../../coach/playerProfile/components/PlayerInfoView';
import { PlayerBioView } from '../../coach/playerProfile/components/PlayerBioView';
import { StatisticsSectionView } from '../../coach/playerProfile/components/StatisticsSectionView';
import { AwardsSectionView } from '../../coach/playerProfile/components/AwardsSectionView';
import { AcademicInfoView } from '../../coach/playerProfile/components/AcademicInfoView';
import adminService from '../../../services/adminService';
import { formatLocation } from '../../../utils/formatLocation';

export const AdminPlayerProfileView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videos, setVideos] = useState([]);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await adminService.getUserDetails(userId);
        setUserData(response.user);
        setVideos(response.user?.videos || []);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load user data');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center min-h-screen">
        <div className="text-jp-ink text-lg">Loading player profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-jp-muted hover:text-jp-ink mb-4 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
          <span className="font-medium">Back to Dashboard</span>
        </button>
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-6">
          <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="w-full p-6">
        <div className="text-jp-ink text-center">User not found</div>
      </div>
    );
  }

  const handleDeleteVideo = async (videoId) => {
    if (!userData?._id) return;
    setDeletingVideoId(videoId);
    try {
      await adminService.deleteUserVideo(userData._id, videoId);
      setVideos((prev) => prev.filter((v) => v._id !== videoId));
      if (playingVideoId === videoId) setPlayingVideoId(null);
    } catch (err) {
      console.error('Failed to delete video:', err);
    } finally {
      setDeletingVideoId(null);
    }
  };

  // Map API data to component format
  const playerData = {
    id: userData._id,
    name: `${userData.firstName || '-'} ${userData.lastName || '-'}`,
    photo: userData.profileImage || null,
    transferStatus: userData.transferStatus || '-',
    class: userData.class || '-',
    bt: userData.batsThrows || '-',
    position: userData.position || '-',
    hometown: userData.hometown || '-',
    commitment: userData.commitmentStatus === 'committed' ? (userData.committedTo || 'Committed') : 'Uncommitted',
    highSchool: userData.highSchool || '-',
    college: userData.previousSchool || userData.team?.name || '-',
    height: userData.height || '-',
    weight: userData.weight ? `${userData.weight} LBS` : '-',
    eligibilityYears: userData.eligibilityYears || [],
    bio: userData.bio || '-',
    gpa: userData.academic_info_gpa || '-',
    sat: userData.academic_info_sat || '-',
    act: userData.academic_info_act || '-',
    classRank: userData.classRank || '-',
    email: userData.email || '-',
    phoneNumber: userData.phoneNumber || '-',
    registrationStatus: userData.registrationStatus || '-',
    jerseyNumber: userData.jerseyNumber || '-',
    teamName: userData.team?.name || '-',
    teamLogo: userData.team?.logo || null,
    teamLocation: formatLocation(userData.team?.location) || '-',
    teamDivision: userData.team?.division || '-',
  };

  return (
    <div className="w-full">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/dashboard')}
        className="flex items-center gap-2 text-jp-muted hover:text-jp-ink mb-4 transition-colors"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        <span className="font-medium">Back to Dashboard</span>
      </button>

      {/* Player Profile Content */}
      <div className="max-w-7xl mx-auto bg-jp-card border border-jp-line shadow-sm px-6 rounded-2xl space-y-6 relative z-20">
        {/* User Registration Info */}
        <div className="pt-6 pb-4 border-b border-jp-line">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-jp-muted mb-1">Email</p>
              <p className="text-sm text-jp-ink">{playerData.email}</p>
            </div>
            <div>
              <p className="text-xs text-jp-muted mb-1">Phone Number</p>
              <p className="text-sm text-jp-ink">{playerData.phoneNumber}</p>
            </div>
            <div>
              <p className="text-xs text-jp-muted mb-1">Registration Status</p>
              <p className={`text-sm font-medium ${
                playerData.registrationStatus === 'approved' ? 'text-green-600 dark:text-green-400' :
                playerData.registrationStatus === 'pending' ? 'text-amber-600 dark:text-yellow-400' :
                'text-red-600 dark:text-red-400'
              }`}>
                {playerData.registrationStatus.toUpperCase()}
              </p>
            </div>
          </div>
        </div>

        {/* Team Info */}
        {playerData.teamName !== '-' && (
          <div className="pt-4 pb-4 border-b border-jp-line">
            <h3 className="text-sm font-semibold text-jp-ink mb-3">TEAM INFORMATION</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-jp-muted mb-1">Team</p>
                <p className="text-sm text-jp-ink">{playerData.teamName}</p>
              </div>
              {playerData.jerseyNumber !== '-' && (
                <div>
                  <p className="text-xs text-jp-muted mb-1">Jersey Number</p>
                  <p className="text-sm text-jp-ink">#{playerData.jerseyNumber}</p>
                </div>
              )}
              {playerData.teamLocation !== '-' && (
                <div>
                  <p className="text-xs text-jp-muted mb-1">Location</p>
                  <p className="text-sm text-jp-ink">{playerData.teamLocation}</p>
                </div>
              )}
              {playerData.teamDivision !== '-' && (
                <div>
                  <p className="text-xs text-jp-muted mb-1">Division</p>
                  <p className="text-sm text-jp-ink">{playerData.teamDivision}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Player Info Fields */}
        <PlayerInfoView playerData={playerData} />

        {/* Video Section */}
        <div className="rounded-2xl border-jp-line">
          <h3 className="text-jp-ink font-bold text-lg mb-4">VIDEO</h3>
          {videos.length === 0 ? (
            <div className="p-6 rounded-xl bg-jp-section border border-jp-line text-center">
              <p className="text-jp-muted text-sm">No videos available</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {videos.map((video, index) => (
                <div key={video._id || index} className="relative aspect-video bg-jp-section rounded-lg overflow-hidden group">
                  {playingVideoId === video._id ? (
                    <video
                      src={video.url || video.videoUrl}
                      controls
                      autoPlay
                      className="w-full h-full object-cover"
                      onEnded={() => setPlayingVideoId(null)}
                    />
                  ) : (
                    <>
                      {video.thumbnailUrl ? (
                        <img src={video.thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-jp-section" />
                      )}
                      <button
                        type="button"
                        onClick={() => setPlayingVideoId(video._id)}
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-10 h-10 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 4l8 6-8 6V4z"/>
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteVideo(video._id)}
                    disabled={deletingVideoId === video._id}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-600/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                  >
                    {deletingVideoId === video._id ? (
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Player Bio */}
        <PlayerBioView bio={playerData.bio} />

        {/* Statistics - Full Width */}
        <div className="pt-12">
          <StatisticsSectionView
            battingStats={userData.battingStats || []}
            fieldingStats={userData.fieldingStats || []}
            pitchingStats={userData.pitchingStats || []}
            position={playerData.position}
            teamName={userData.team && typeof userData.team === 'object' ? userData.team.name : (typeof userData.team === 'string' ? userData.team : null)}
          />
        </div>

        {/* Previous Teams | Awards | Coach Recommendation - 3 column row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {/* Previous Teams */}
          <div className="border border-jp-line rounded-2xl p-5 flex flex-col">
            <h3 className="text-jp-ink font-bold text-lg mb-4">PREVIOUS TEAMS</h3>
            <div className="flex flex-wrap gap-3 border border-jp-line rounded-2xl p-2 min-h-[48px]">
              {userData?.previousTeams ? (
                String(userData.previousTeams).split(',').map((t) => t.trim()).filter(Boolean).map((team, i) => (
                  <span key={i} className="px-4 py-2 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-400 text-sm font-medium">
                    {team}
                  </span>
                ))
              ) : (
                <p className="text-jp-muted text-sm px-2 py-1">No previous teams</p>
              )}
            </div>
          </div>

          {/* Awards */}
          <div className="border border-jp-line rounded-2xl p-5 flex flex-col">
            <AwardsSectionView awards={userData.awardsAchievements || []} />
          </div>

          {/* Coach Recommendation placeholder */}
          <div className="border border-jp-line rounded-2xl p-5 flex flex-col">
            <h3 className="text-jp-ink font-bold text-lg mb-4">COACH RECOMMENDATION</h3>
            {userData?.coachRecommendation?.documentUrl ? (
              <a href={userData.coachRecommendation.documentUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-jp-section rounded-xl border border-jp-line hover:border-cyan-500/50 transition-colors group">
                <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-900 text-xs font-bold">PDF</span>
                </div>
                <span className="text-jp-muted text-sm truncate group-hover:text-jp-ink transition-colors">
                  {userData.coachRecommendation.filename || 'Coach Recommendation'}
                </span>
                <svg className="w-4 h-4 text-jp-muted group-hover:text-jp-accent-ink flex-shrink-0 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </a>
            ) : (
              <p className="text-jp-muted text-sm">No coach recommendation uploaded.</p>
            )}
          </div>
        </div>

        {/* Academic Info */}
        <AcademicInfoView academicData={playerData} />
      </div>
    </div>
  );
};

export default AdminPlayerProfileView;
